/**
 * Plain-language fallback messages for form validation.
 * Zod 4's default English messages read like type errors ("Invalid input: expected string,
 * received undefined"); this maps the cases forms hit to the wording users saw under Zod 3.
 * Messages set on a schema (`.min(3, '...')`) still win over these.
 */
import * as z from 'zod'

const isBlank = (input: unknown) => input === undefined || input === null || input === ''

const sizeMessage = (issue: z.core.$ZodRawIssue, bound: 'min' | 'max'): string | undefined => {
  const limit = Number(bound === 'min' ? (issue as any).minimum : (issue as any).maximum)
  const inclusive = (issue as any).inclusive !== false
  switch ((issue as any).origin) {
    case 'string':
      if (bound === 'min' && limit <= 1) return 'Required'
      return `Must be ${bound === 'min' ? 'at least' : 'at most'} ${limit} character${limit === 1 ? '' : 's'}`
    case 'number':
    case 'int':
      if (bound === 'min') return inclusive ? `Must be at least ${limit}` : `Must be greater than ${limit}`
      return inclusive ? `Must be at most ${limit}` : `Must be less than ${limit}`
    case 'array':
      return `Must have ${bound === 'min' ? 'at least' : 'at most'} ${limit} item${limit === 1 ? '' : 's'}`
    default:
      return undefined
  }
}

z.config({
  customError: (issue) => {
    switch (issue.code) {
      case 'invalid_type':
        if (isBlank(issue.input)) return 'Required'
        if (issue.expected === 'number') return 'Must be a number'
        if (issue.expected === 'date') return 'Invalid date'
        return undefined
      case 'too_small':
        return sizeMessage(issue, 'min')
      case 'too_big':
        return sizeMessage(issue, 'max')
      case 'invalid_value':
        return isBlank(issue.input) ? 'Required' : 'Select one of the available options'
      default:
        return undefined
    }
  },
})
